import { useMutation } from "@tanstack/react-query";
import { CircleAlert } from "lucide-react";
import { Button } from "@altek/ui/components/ui/Button";
import { useScreenTitle } from "@/app/layouts/document-title";
import { sessionApi, type Session } from "@/features/auth/api/session.api";
import { AuthCardScreen } from "@/features/auth/components/AuthShell";

/**
 * The screen a customer sees when their subscription has lapsed, been suspended, or never
 * existed.
 *
 * INSTEAD OF THE APPLICATION, not beside it. A blocked customer left inside the shell clicks
 * into screens that each answer 402 separately; what they learn from that is that the product is
 * broken, not that their subscription ended — and every one of those clicks is a request the
 * server does work to refuse. One sentence, once, in front of everything.
 *
 * THEY CAN STILL SIGN OUT, and that is deliberate: a screen with no way off it is a trap, and
 * the person looking at it may not be the person who deals with the invoice. Signing out lets a
 * colleague who can sign in on the same machine.
 *
 * `onSignedOut` IS A PROP, not `useAuth()` read from inside. The shell already holds `reset`
 * and this component holds no other context — taking it in keeps the component renderable on
 * its own, which is the difference between a test that exercises the screen and a test that
 * spends its setup assembling a provider tree to reach three sentences.
 *
 * IT DOES NOT SAY WHAT TO DO ABOUT IT. What to do depends on a commercial conversation — a
 * renewal, a payment, a negotiation — and putting "aboneliğinizi yenileyin" here would promise a
 * button this product does not have. The server's sentence says WHICH of the three things
 * happened, which is the part somebody needs before they can pick up the telephone.
 */
export function SubscriptionBlocked({
  session,
  onSignedOut,
}: {
  session: Session;
  onSignedOut: () => void;
}) {
  const signOut = useMutation({
    mutationFn: () => sessionApi.logout(),
    // `onSettled`, not `onSuccess`: a failed sign-out request still has to clear the client, or
    // somebody who cannot reach the server is stuck on this screen with a button that does
    // nothing.
    onSettled: () => onSignedOut(),
  });

  const customer = session.tenant.current;
  // Uygulamanın YERİNE çizilen ekran: sayfanın tek `<h1>`'i ve sekmenin adı bu (9 Ekim 2026,
  // 5. tur). Kitin `CardHeader`'ı başlığı `<h2>` çiziyor; görünüşü aşağıda aynen korunuyor.
  // SEKME ADI BUGÜN EZİLİYOR: `AppLayout` bu ekranı `ScreenTitleContext`'in dışında çiziyor ve
  // kendi başlık etkisi (ebeveyn, sonra çalışıyor) adresin adını yazıyor — kabuğa bildirildi.
  useScreenTitle("Aboneliğiniz kullanıma kapalı");

  return (
    <AuthCardScreen width="max-w-md">
      <div className="flex flex-col gap-4">
        <div>
          <span className="mb-4 grid size-10 place-items-center rounded-lg bg-danger-bg text-danger">
            <CircleAlert aria-hidden className="size-5" />
          </span>
          <h1 className="text-xl font-semibold text-fg">Aboneliğiniz kullanıma kapalı</h1>
          {customer ? <p className="mt-1 text-base text-fg-muted">{customer.displayName ?? customer.name}</p> : null}
        </div>
        {/**
         * The SERVER's sentence, not one composed here. It names which of the three things
         * happened — no subscription, expired on a date, suspended — and a client that
         * re-worded it would eventually say something the server does not mean.
         */}
        <p className="text-base text-fg">{session.subscription.message}</p>
        <p className="text-base text-fg-muted">
          Bu kurulumu kullanmaya devam etmek için ALTEK ile iletişime geçin. Verileriniz duruyor; abonelik yeniden
          açıldığında kaldığınız yerden devam edersiniz.
        </p>
        <Button variant="secondary" size="touch" block loading={signOut.isPending} onClick={() => signOut.mutate()}>
          Çıkış yap
        </Button>
      </div>
    </AuthCardScreen>
  );
}
